/**
 * Hook for processing word cloud data
 * Handles filtering, spell correction, case merging, and word aggregation
 * Ported from words_cloud_model.coffee calc() method
 */

import { useMemo } from 'react'

import { buildWordGraph } from '@aytm/app/stats/page/question-views/components/word-cloud/utils/wordGraph'

/**
 * Check if word passes stop level filters
 */
function isStopLevelCorrect(word, config) {
  const { stop1, stop2, stop3, stop4, stop5, junk } = config

  // Stop level filters
  if (stop1 === 1 && word[2] === 1) return false
  if (stop2 === 1 && word[2] === 2) return false
  if (stop3 === 1 && word[2] === 3) return false

  // Numeric filter
  if (stop4 === 1 && !Number.isNaN(parseFloat(word[0])) && isFinite(word[0])) return false

  // Single letter filter
  if (stop5 === 1 && word[0].length === 1) return false

  // Junk filter
  if (!junk && word[3]) return false

  return true
}

/**
 * Remove scripts from text (security)
 */
function removeScripts(text) {
  if (!text) return ''
  return text.replace(/<script[^>]*>[\S\s]*?<\/script>/gi, '')
}

/**
 * Decode HTML entities
 */
function decodeText(text) {
  if (!text || typeof document === 'undefined') return text
  const el = document.createElement('textarea')
  el.innerHTML = text
  return el.value
}

/**
 * Hook to process words for word cloud display
 *
 * @param {Object} rawData - Raw word data from API
 * @param {Object} config - Processing configuration
 * @param {boolean} [useRawData=false] - Bypass text processing; sourced from `model.useRawData` at the caller, NOT from `config`. Kept as an explicit argument so the hook is agnostic to where the flag lives in the parent's prop tree.
 * @returns {Object} Processed data { words, graph }
 */
export function useWordProcessor(rawData, config, useRawData = false) {
  const {
    activeWord,
    maxWords = 300,
    spell: spellEnabled = 1,
    caps: capsEnabled = 1,
    mergeWords: mergeEnabled = 1,
    twoWords: twoWordsEnabled = 0,
    blackList = [],
    junk
  } = config

  return useMemo(() => {
    if (!rawData?.words?.length) {
      return { words: [], graph: null }
    }

    const { words, sentences = [], spell = {}, noun = {}, index = {}, caseIndex = {} } = rawData

    const blackListHash = {}
    for (const word of blackList) {
      blackListHash[word] = true
    }

    // Raw data mode - just return filtered words
    if (useRawData) {
      const result = words
        .filter(word => junk || word[3])
        .map(word => {
          return {
            id: word[2],
            text: removeScripts(decodeText(word[0])),
            times: word[1],
            f: word[1]
          }
        })
        .filter(w => 0 < w.text.length)
        .sort((a, b) => b.times - a.times)

      return { words: result, graph: null }
    }

    // Two-word phrases mode
    if (twoWordsEnabled === 1) {
      const graph = buildWordGraph(sentences, words, word => isStopLevelCorrect(word, config))

      // Apply merges
      if (capsEnabled === 1) {
        for (const word in caseIndex) {
          if (caseIndex[word].length > 1) {
            const target = words[caseIndex[word][0]][0]
            for (let i = 1; i < caseIndex[word].length; i++) {
              graph.merge(words[caseIndex[word][i]][0], target)
            }
          }
        }
      }

      if (spellEnabled === 1) {
        for (const word in spell) {
          if (spell[word] && graph.isNode(word)) {
            graph.merge(word, spell[word])
          }
        }
      }

      if (mergeEnabled === 1) {
        for (const word in noun) {
          if (noun[word] && graph.isNode(word)) {
            graph.merge(word, noun[word])
          }
        }
      }

      let result
      if (activeWord === '') {
        // Show top two-word phrases
        result = graph
          .topWeights(Math.round(maxWords / 2))
          .filter(item => !blackListHash[item.text])
          .map(item => {
            return {
              text: removeScripts(decodeText(item.text)),
              times: item.weight,
              f: item.times
            }
          })
          .filter(w => w.text.length > 0)
          .sort((a, b) => b.times - a.times)
          .slice(0, maxWords)
      } else {
        // Show words related to active word
        const parts = activeWord.split(' ')

        if (parts.length === 2) {
          // Two-word active word - find related single words
          const node1 = graph.node(parts[0])
          const node2 = graph.node(parts[1])
          const combined = {}
          const counts = {}

          for (const word in node1.join) {
            if (blackListHash[word] || word === parts[0] || word === parts[1]) continue
            const w = graph.weight(node1.name, word)
            combined[word] = w.weight
            counts[word] = w.times
          }

          for (const word in node2.join) {
            if (blackListHash[word] || word === parts[0] || word === parts[1]) continue
            const w = graph.weight(node2.name, word)
            if (!combined[word]) {
              combined[word] = 0
              counts[word] = 0
            }
            combined[word] += w.weight
            counts[word] += w.times
          }

          result = Object.keys(combined)
            .map(word => {
              return {
                text: removeScripts(decodeText(word)),
                times: combined[word],
                f: counts[word]
              }
            })
            .filter(w => w.text.length > 0)
            .sort((a, b) => b.times - a.times)
            .slice(0, maxWords)

          // Calculate main word weight
          let mainWeight = 0
          let mainF = 0
          if (result.length > 0) {
            for (const item of result) {
              mainWeight += item.times
              mainF += item.f
            }
            if (mainWeight > result[0].times * 3) {
              mainWeight = result[0].times * 3
            }
          } else {
            mainWeight = 1
            mainF = 1
          }

          result = [{ text: removeScripts(decodeText(activeWord)), times: mainWeight, f: mainF }, ...result]
        } else {
          // Single word active - show two-word phrases containing it
          const node = graph.node(activeWord)
          result = []

          for (const word in node.join) {
            if (blackListHash[word]) continue
            const w = graph.weight(node.name, word)
            const text = w.d12 > w.d21 ? `${node.name} ${word}` : `${word} ${node.name}`
            result.push({
              text: removeScripts(decodeText(text)),
              times: w.weight,
              f: w.times
            })
          }

          result = result
            .filter(w => w.text.length > 0)
            .sort((a, b) => b.times - a.times)
            .slice(0, maxWords)
        }
      }

      return { words: result, graph }
    }

    // Standard mode - single words
    if (activeWord === '') {
      // Aggregate words
      const draft = {}

      for (const word of words) {
        if (blackListHash[word[0]]) continue
        if (!isStopLevelCorrect(word, config)) continue

        let text = word[0]
        const times = word[1]

        // Apply spell correction
        if (spellEnabled === 1 && spell[text.toLowerCase()]) {
          text = spell[text.toLowerCase()]
        }

        // Apply noun lemmatization
        if (mergeEnabled === 1) {
          const lowText = text.toLowerCase()
          if (noun[lowText]) {
            text = text === text.toUpperCase() ? noun[lowText].toUpperCase() : noun[lowText]
          }
        }

        // Group by case
        const key = capsEnabled === 1 ? text.toLowerCase() : text

        if (draft[key]) {
          draft[key].times += times
        } else {
          draft[key] = {
            id: word[2],
            text: removeScripts(decodeText(text)),
            times,
            f: times
          }
        }
      }

      const result = Object.values(draft)
        .filter(w => !blackListHash[w.text] && w.text.length > 0)
        .sort((a, b) => b.times - a.times)
        .slice(0, maxWords)

      return { words: result, graph: null }
    }

    // Active word mode - show related words
    const graph = buildWordGraph(sentences, words, word => isStopLevelCorrect(word, config))

    // Apply merges
    if (capsEnabled === 1) {
      for (const word in caseIndex) {
        if (caseIndex[word].length > 1) {
          const target = words[caseIndex[word][0]][0]
          for (let i = 1; i < caseIndex[word].length; i++) {
            graph.merge(words[caseIndex[word][i]][0], target)
          }
        }
      }
    }

    if (spellEnabled === 1) {
      for (const word in spell) {
        if (spell[word] && graph.isNode(word)) {
          graph.merge(word, spell[word])
        }
      }
    }

    if (mergeEnabled === 1) {
      for (const word in noun) {
        if (noun[word] && graph.isNode(word)) {
          graph.merge(word, noun[word])
        }
      }
    }

    const node = graph.node(activeWord)
    let result = []

    for (const word in node.join) {
      if (blackListHash[word]) continue
      const w = graph.weight(node.name, word)
      result.push({
        text: removeScripts(decodeText(word)),
        times: w.weight,
        f: w.times
      })
    }

    result = result
      .filter(w => w.text.length > 0)
      .sort((a, b) => b.times - a.times)
      .slice(0, maxWords)

    // Add main word
    let mainWeight = 0
    let mainF = 0
    if (result.length > 0) {
      for (const item of result) {
        mainWeight += item.times
        mainF += item.f
      }
      if (mainWeight > result[0].times * 3) {
        mainWeight = result[0].times * 3
      }
    } else {
      const idx = index[node.name]
      if (idx !== undefined) {
        mainWeight = words[idx][1]
        mainF = mainWeight
      } else {
        mainWeight = 1
        mainF = 1
      }
    }

    result = [{ text: removeScripts(decodeText(activeWord)), times: mainWeight, f: mainF }, ...result]

    return { words: result, graph }
  }, [
    rawData,
    config,
    maxWords,
    spellEnabled,
    capsEnabled,
    mergeEnabled,
    twoWordsEnabled,
    activeWord,
    blackList,
    useRawData,
    junk
  ])
}

export default useWordProcessor
